/**
 * Notes Store - Manages notes state
 */

import { create } from 'zustand';
import { notesApi, Note } from '../services/api';

// Re-export Note type for convenience
export type { Note };

interface NotesState {
  notes: Note[];
  currentDocumentNotes: Note[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;

  // Actions
  fetchAllNotes: (token: string) => Promise<void>;
  fetchDocumentNotes: (documentId: string, token: string) => Promise<void>;
  saveNote: (note: Partial<Note>, token: string) => Promise<string>;
  updateNote: (noteId: string, note: Partial<Note>, token: string) => Promise<void>;
  deleteNote: (noteId: string, token: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  clearError: () => void;
}

const useNotesStoreBase = create<NotesState>((set, get) => ({
  notes: [],
  currentDocumentNotes: [],
  isLoading: false,
  error: null,
  searchQuery: '',

  fetchAllNotes: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await notesApi.getNotes(token);
      set({ notes: response.notes || [], isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false, notes: [] });
    }
  },

  fetchDocumentNotes: async (documentId: string, token: string) => {
    try {
      const response = await notesApi.getNotes(token, documentId);
      set({ currentDocumentNotes: response.notes || [] });
    } catch (error) {
      console.error('Failed to fetch document notes:', error);
      set({ currentDocumentNotes: [] });
    }
  },

  saveNote: async (note: Partial<Note>, token: string) => {
    try {
      const response = await notesApi.saveNote(note, token);
      // Refresh notes
      if (note.document_id) {
        await get().fetchDocumentNotes(note.document_id, token);
      }
      await get().fetchAllNotes(token);
      return response.noteId;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updateNote: async (noteId: string, note: Partial<Note>, token: string) => {
    try {
      await notesApi.updateNote(noteId, note, token);
      // Refresh notes
      await get().fetchAllNotes(token);
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deleteNote: async (noteId: string, token: string) => {
    try {
      await notesApi.deleteNote(noteId, token);
      set((state) => ({
        notes: state.notes.filter((n) => n.id !== noteId),
        currentDocumentNotes: state.currentDocumentNotes.filter((n) => n.id !== noteId),
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Safe hook that guarantees arrays are always arrays
export const useNotesStore = () => {
  const state = useNotesStoreBase();
  return {
    ...state,
    // Guarantee arrays are always arrays
    notes: Array.isArray(state.notes) ? state.notes : [],
    currentDocumentNotes: Array.isArray(state.currentDocumentNotes) ? state.currentDocumentNotes : [],
  };
};

